import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import './Radio.css';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible text label associated with the radio via htmlFor/id */
  label: string;
  /** Group name — radios sharing a name form one arrow-key-operable group */
  name: string;
  /** Optional id override; if not provided, one is generated via React's useId() */
  id?: string;
  /** Optional class name applied to the outer wrapper element */
  className?: string;
  /** Optional class name applied to the <input> element itself */
  inputClassName?: string;
}

/**
 * One option of a pick-one choice. Native `<input type="radio">`, so the browser provides
 * the radio role, arrow-key movement within the group and checked state (WCAG 2.1.1, 4.1.2).
 * Wrap a set in a `<fieldset>` with a `<legend>` (or `role="radiogroup"` + aria-labelledby).
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ label, id, className, inputClassName, ...rest }, ref) => {
    const generatedId = useId();
    const resolvedId = id ?? `rg-radio-${generatedId}`;
    const rootClassName = ['rg-radio', className].filter(Boolean).join(' ');
    const inputClassNameResolved = ['rg-radio__input', inputClassName].filter(Boolean).join(' ');

    return (
      <div className={rootClassName}>
        <input ref={ref} type="radio" id={resolvedId} className={inputClassNameResolved} {...rest} />
        <label htmlFor={resolvedId} className="rg-radio__label">
          {label}
        </label>
      </div>
    );
  },
);

Radio.displayName = 'Radio';
